import { RefObject, useRef } from 'react';

import { ILoyaltyUserFragment, LoyaltyPromotionType } from 'generated/graphql-gateway';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { DIFFERENCE_TIME_UNITS, getDifferenceToNow } from 'utils/dateTime';

import { TLoyaltyCooldown } from '../types';

import { IUseLoyaltyCooldown } from './types';

const INACTIVE_COOLDOWN: TLoyaltyCooldown = { isActive: false };

const getCooldownInfo = (
  incentiveType: LoyaltyPromotionType,
  isCooldownActive: boolean,
  availableAfter: string,
  enableRewardCooldown?: boolean,
  enableOffersCooldown?: boolean
): TLoyaltyCooldown => {
  if (!isCooldownActive) {
    return INACTIVE_COOLDOWN;
  }

  if (incentiveType === LoyaltyPromotionType.REWARD && !enableRewardCooldown) {
    return INACTIVE_COOLDOWN;
  }

  if (incentiveType === LoyaltyPromotionType.OFFER && !enableOffersCooldown) {
    return INACTIVE_COOLDOWN;
  }

  return { isActive: true, endsAt: new Date(availableAfter) };
};

const scheduleCooldownCalculation = ({
  incentiveType,
  isCooldownActive,
  availableAfter,
  pendingTimeoutRef,
  refetchUser,
}: {
  incentiveType: LoyaltyPromotionType;
  isCooldownActive: boolean;
  availableAfter: string;
  pendingTimeoutRef: RefObject<NodeJS.Timeout | null>;
  refetchUser: () => void;
}) => {
  if (isCooldownActive) {
    /**
     * If the cooldown is active, we DO want to check the state again at least on
     * the next clock tick
     */
    const msToEndOfCooldown = Math.max(
      getDifferenceToNow(DIFFERENCE_TIME_UNITS.MILLISECONDS, availableAfter),
      1
    );

    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
    }

    pendingTimeoutRef.current = setTimeout(() => {
      refetchUser();
    }, msToEndOfCooldown);
  }
};

export const useLoyaltyCooldown = (
  loyaltyUser: ILoyaltyUserFragment | null | undefined,
  refetchUser: () => void
): IUseLoyaltyCooldown => {
  const enableRewardCooldown = useFlag(LaunchDarklyFlag.ENABLE_REWARD_COOLDOWN);
  const enableOffersCooldown = useFlag(LaunchDarklyFlag.ENABLE_GLOBAL_OFFERS_COOLDOWN);

  const pendingTimeouts: Partial<Record<
    LoyaltyPromotionType,
    React.RefObject<NodeJS.Timeout | null>
  >> = {
    [LoyaltyPromotionType.OFFER]: useRef<NodeJS.Timeout | null>(null),
    [LoyaltyPromotionType.REWARD]: useRef<NodeJS.Timeout | null>(null),
  };

  const [offersCooldown, rewardsCooldown] = [
    LoyaltyPromotionType.OFFER,
    LoyaltyPromotionType.REWARD,
  ].map(incentiveType => {
    const availabilityInfo =
      loyaltyUser?.[
        incentiveType === LoyaltyPromotionType.OFFER
          ? 'offerRedemptionAvailability'
          : 'rewardRedemptionAvailability'
      ] || {};

    const isCooldownActive = availabilityInfo.isCooldownActive ?? false;
    const availableAfter = availabilityInfo.availableAfter ?? '';

    scheduleCooldownCalculation({
      incentiveType,
      isCooldownActive,
      availableAfter,
      pendingTimeoutRef: pendingTimeouts[incentiveType]!,
      refetchUser,
    });

    return getCooldownInfo(
      incentiveType,
      isCooldownActive,
      availableAfter,
      enableRewardCooldown,
      enableOffersCooldown
    );
  });

  return { offersCooldown, rewardsCooldown };
};
